import { axiosInstance } from '../../../service/axiosManager'

// The concept probes a study condition shows once its coding time is up (backend: GET and
// POST /api/v1/probes). While the student answers, the keys stay on the server: these calls
// carry the questions and which items the student has answered, never whether an answer was
// right. Once the server has every answer, the GET also brings the review - each answer with
// its key and explanation - which the Thank You overlay shows (ProbeReview).

const PROBES_URL = process.env.REACT_APP_HOST_API + '/probes'

// Waits between attempts, as SessionContext's first fetch: a request is retried until the
// server answers, so a dropped connection loses nothing. Jittered so a class whose network
// comes back at once does not retry in lockstep.
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000, 30000]
export const retryDelay = (attempt) =>
  RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)] * (0.75 + Math.random() * 0.5)

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// Item ids as the page compares them. An answer carries the item's own id back, as sent.
export const idKey = (id) => String(id)

const isText = (value) => typeof value === 'string'

// The GET reply's review, as ProbeReview uses it: [{ itemId, choice, correct, key,
// explanation }], or null - before the last answer the server sends none, and an entry of an
// unknown shape is left out.
export const normalizeReview = (review) => {
  if (!Array.isArray(review)) return null
  const entries = review
    .filter(
      (entry) =>
        entry && (isText(entry.itemId) || Number.isFinite(entry.itemId)) && isText(entry.key) && isText(entry.choice)
    )
    .map((entry) => ({
      itemId: entry.itemId,
      choice: entry.choice,
      correct: entry.correct === true,
      key: entry.key,
      explanation: isText(entry.explanation) ? entry.explanation : '',
    }))
  return entries.length ? entries : null
}

// The GET reply as the modal uses it: items in their numbered order, each with its blocks
// and options. A block or option of an unknown shape is left out rather than shown half
// built, and an item without options is left out because it could never be answered.
export const normalizeProbes = (data) => {
  const items = (Array.isArray(data?.items) ? data.items : [])
    .filter((item) => item && (isText(item.id) || Number.isFinite(item.id)))
    .map((item) => ({
      id: item.id,
      number: Number(item.number),
      blocks: (Array.isArray(item.blocks) ? item.blocks : []).filter(
        (block) => (block?.type === 'text' && isText(block.text)) || (block?.type === 'code' && isText(block.code))
      ),
      options: (Array.isArray(item.options) ? item.options : []).filter(
        (option) => isText(option?.letter) && isText(option?.text)
      ),
    }))
    .filter((item) => item.options.length > 0)
  // Numbered order when every item has a number, otherwise the order the server sent.
  if (items.every((item) => Number.isFinite(item.number))) items.sort((a, b) => a.number - b.number)
  return {
    probeSet: isText(data?.probeSet) ? data.probeSet : null,
    version: isText(data?.version) ? data.version : null,
    items,
    answeredItemIds: (Array.isArray(data?.answeredItemIds) ? data.answeredItemIds : []).map(idKey),
    review: normalizeReview(data?.review),
    // Whether the server has this student's survey (the Thank You overlay's, StudySurvey).
    surveyAnswered: data?.surveyAnswered === true,
  }
}

// GET /probes/:sessionId -> normalizeProbes' shape. Any failure throws and the caller tries
// again, a 404 included: the page only asks for a condition whose row names a probe set, so
// a 404 means a server that cannot answer yet (a backend deploy without the probes, say),
// not a student with nothing to answer.
export const fetchProbes = async (sessionId, userId) => {
  const res = await axiosInstance.get(`${PROBES_URL}/${sessionId}`, { params: { userId } })
  return normalizeProbes(res.data)
}

// POST /probes/:sessionId/answers -> the item ids the server has answers for. A 200 means
// this item is stored, by this request or an earlier one: sending an item again is harmless,
// the server keeps the first answer.
export const postProbeAnswer = async (sessionId, answer) => {
  const res = await axiosInstance.post(`${PROBES_URL}/${sessionId}/answers`, answer)
  const saved = (Array.isArray(res.data?.answeredItemIds) ? res.data.answeredItemIds : []).map(idKey)
  return saved.includes(idKey(answer.itemId)) ? saved : [...saved, idKey(answer.itemId)]
}

// One id per answer, sent with every attempt at it, so the server can tell a resend from a
// new answer. randomUUID needs a secure page (https or localhost); elsewhere a time-and-random
// id does the same job.
export const newClientEventId = () => {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : undefined
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`
}

// Answers the server has not confirmed yet stay in this browser until it has, so a reload, or
// a page closed while offline, sends them on the next load. One key per session, student and
// item, like CodeContext's code backups. Storage can be missing or full (private windows), so
// every access is guarded: the answer then lives as long as the page.
const pendingKey = (sessionId, userId, itemId) => `probe_answer_${sessionId}_${userId}_${itemId}`

export const storePendingAnswer = (sessionId, answer) => {
  try {
    localStorage.setItem(pendingKey(sessionId, answer.userId, answer.itemId), JSON.stringify(answer))
  } catch (error) {
    // Kept in memory only.
  }
}

export const readPendingAnswer = (sessionId, userId, itemId) => {
  try {
    const answer = JSON.parse(localStorage.getItem(pendingKey(sessionId, userId, itemId)))
    return answer && typeof answer === 'object' && isText(answer.choice) && idKey(answer.itemId) === idKey(itemId)
      ? answer
      : null
  } catch (error) {
    return null
  }
}

// When each item was first shown, kept the same way, so a reload in the middle of a question
// does not restart its response time.
const shownKey = (sessionId, userId, itemId) => `probe_shown_${sessionId}_${userId}_${itemId}`

export const storeShownAt = (sessionId, userId, itemId, shownAt) => {
  try {
    localStorage.setItem(shownKey(sessionId, userId, itemId), shownAt)
  } catch (error) {
    // Kept in memory only: a reload then starts the time again, as before.
  }
}

// The ISO time stored for the item, or null for none, or for a value that is not a time.
export const readShownAt = (sessionId, userId, itemId) => {
  try {
    const shownAt = localStorage.getItem(shownKey(sessionId, userId, itemId))
    return isText(shownAt) && Number.isFinite(Date.parse(shownAt)) ? shownAt : null
  } catch (error) {
    return null
  }
}

// Once the server has an item's answer, this browser's copies are done with: the answer, and
// when the item was first shown.
export const forgetPendingAnswer = (sessionId, userId, itemId) => {
  try {
    localStorage.removeItem(pendingKey(sessionId, userId, itemId))
    localStorage.removeItem(shownKey(sessionId, userId, itemId))
  } catch (error) {
    // Nothing was stored.
  }
}

// One more send of an answer the server has not confirmed, for a page that may be going away
// (pagehide, or hidden on a phone): fetch's keepalive is finished by the browser after the
// page is gone, where an axios request would be cancelled - the same request telemetryService
// sends then. Same body, so same clientEventId: to the server it is one more resend. It
// carries the sign-in header axios would send. Nothing waits on the reply: if the page stays,
// sendUntilSaved is still the one that confirms the answer.
export const sendAnswerKeepalive = (sessionId, answer) => {
  const authorization = axiosInstance.defaults?.headers?.common?.Authorization
  try {
    fetch(`${PROBES_URL}/${sessionId}/answers`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', ...(authorization ? { Authorization: authorization } : {}) },
      body: JSON.stringify(answer),
    }).catch(() => {})
  } catch (error) {
    // No fetch, or the body refused: the stored copy goes out on the next page load.
  }
}

// Sends one answer until the server confirms it, then forgets the stored copy. Resolves to
// the item ids the server has answers for, or to null when `isCancelled` stops it first (the
// modal closed: the stored copy stays for the next page load). `onRetry` hears each failure.
export const sendUntilSaved = async (sessionId, answer, { isCancelled = () => false, onRetry, wait = sleep } = {}) => {
  for (let attempt = 0; !isCancelled(); attempt++) {
    try {
      const saved = await postProbeAnswer(sessionId, answer)
      forgetPendingAnswer(sessionId, answer.userId, answer.itemId)
      return saved
    } catch (error) {
      onRetry?.(error)
      await wait(retryDelay(attempt))
    }
  }
  return null
}

// The short survey on the Thank You overlay (StudySurvey, useStudySurvey): POST
// /probes/:sessionId/survey { userId, rating, feedback, clientEventId }. A 200 means it is
// stored, by this request or an earlier one (the server keeps the first). A 400 is a survey the
// server refuses (a bad rating), which no resend fixes.
export const SURVEY_FEEDBACK_MAX = 2000

export const postSurvey = async (sessionId, survey) => {
  const { userId, rating, feedback, clientEventId } = survey
  await axiosInstance.post(`${PROBES_URL}/${sessionId}/survey`, { userId, rating, feedback, clientEventId })
}

// The survey the student submitted, kept in this browser like the probe answers: the body sent,
// and saved once the server confirmed it. A reload then shows it as submitted, and sends it
// again while not saved.
const surveyKey = (sessionId, userId) => `probe_survey_${sessionId}_${userId}`

export const storeSurvey = (sessionId, survey) => {
  try {
    localStorage.setItem(surveyKey(sessionId, survey.userId), JSON.stringify(survey))
  } catch (error) {
    // Kept in memory only.
  }
}

// The stored survey, or null for none, or for one of an unknown shape.
export const readSurvey = (sessionId, userId) => {
  try {
    const survey = JSON.parse(localStorage.getItem(surveyKey(sessionId, userId)))
    return survey && typeof survey === 'object' && Number.isInteger(survey.rating) && isText(survey.feedback)
      ? survey
      : null
  } catch (error) {
    return null
  }
}

export const forgetSurvey = (sessionId, userId) => {
  try {
    localStorage.removeItem(surveyKey(sessionId, userId))
  } catch (error) {
    // Nothing was stored.
  }
}

// Sends the survey until the server confirms it (as sendUntilSaved does an answer), then marks
// the stored copy saved. Resolves to true once saved, false when the server refused it (400:
// the stored copy is dropped, so the student can submit again), or null when `isCancelled`
// stops it first (the stored copy stays for the next page load).
export const sendSurveyUntilSaved = async (sessionId, survey, { isCancelled = () => false, onRetry, wait = sleep } = {}) => {
  for (let attempt = 0; !isCancelled(); attempt++) {
    try {
      await postSurvey(sessionId, survey)
      storeSurvey(sessionId, { ...survey, saved: true })
      return true
    } catch (error) {
      if (error?.response?.status === 400) {
        forgetSurvey(sessionId, survey.userId)
        return false
      }
      onRetry?.(error)
      await wait(retryDelay(attempt))
    }
  }
  return null
}
